import { useCallback, useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { LogIn, ShieldCheck, ShoppingCart, Truck } from "lucide-react";

export const BASE = process.env.REACT_APP_BACKEND_URL;
export const INK = "#0B0B0B";
export const GREEN = "#4CCC06";
export const PAPER = "#F4F2EE";
export const head = { fontFamily: "'Barlow Condensed', sans-serif" };
export const body = { fontFamily: "'Barlow', sans-serif" };
export const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const KEY = "alr_shop_cart";

export const shopFetch = async (path, opts = {}) => {
  const res = await fetch(`${BASE}/api/public-store${path}`, { ...opts, headers: { "Content-Type": "application/json", ...(opts.headers || {}) } });
  const j = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(typeof j.detail === "string" ? j.detail : "Something went wrong. Please try again.");
  return j;
};

const read = () => { try { return JSON.parse(localStorage.getItem(KEY)) || []; } catch { return []; } };

export const useCart = () => {
  const [cart, setCart] = useState(read);
  useEffect(() => { const on = () => setCart(read()); window.addEventListener("alr-cart", on); window.addEventListener("storage", on); return () => { window.removeEventListener("alr-cart", on); window.removeEventListener("storage", on); }; }, []);
  const save = useCallback((next) => { localStorage.setItem(KEY, JSON.stringify(next)); window.dispatchEvent(new Event("alr-cart")); }, []);
  const add = (p, q = 1) => { const c = read(); const i = c.findIndex((x) => x.product_id === p.id); const max = p.fulfillment === "lso_course" ? 1 : 50; if (i >= 0) c[i].quantity = Math.min(max, c[i].quantity + q); else c.push({ product_id: p.id, quantity: Math.min(max, q) }); save(c); };
  const setQty = (id, q, max = 50) => save(read().map((x) => (x.product_id === id ? { ...x, quantity: Math.max(1, Math.min(max, q)) } : x)));
  const remove = (id) => save(read().filter((x) => x.product_id !== id));
  const clear = () => save([]);
  return { cart, count: cart.reduce((a, b) => a + b.quantity, 0), add, setQty, remove, clear };
};

export const useInfo = () => {
  const [info, setInfo] = useState(null);
  useEffect(() => { shopFetch("/info").then(setInfo).catch(() => setInfo({ store_open: true, shipping_flat_usd: 0, free_shipping_over_usd: 0 })); }, []);
  return info;
};

export const GROUPS = [
  { slug: "safety-gear", label: "Safety gear", cats: ["bundle", "safety", "training"] },
  { slug: "laser-machines", label: "Laser machines", cats: ["laser_machine"] },
];
export const groupOf = (cat) => GROUPS.find((g) => g.cats.includes(cat))?.slug;

const Header = ({ count }) => (
  <header className="sticky top-0 z-30 border-b border-white/10 bg-[#0B0B0B]/95 backdrop-blur-md">
    <div className="mx-auto flex max-w-6xl items-center gap-4 px-5 py-3 sm:px-8">
      <Link to="/shop" className="flex items-center gap-3" data-testid="shop-logo"><img src="/alr-logo-horizontal-white.png" alt="Advanced Laser Restoration" className="h-8 w-auto" /></Link>
      <nav className="hidden items-center gap-1 md:flex" data-testid="shop-nav">
        {GROUPS.map((g) => <NavLink key={g.slug} to={`/shop/c/${g.slug}`} data-testid={`shop-nav-${g.slug}`}
          className={({ isActive }) => `rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-[2px] transition-colors ${isActive ? "bg-white/10 text-[#4CCC06]" : "text-white/60 hover:text-white"}`} style={head}>{g.label}</NavLink>)}
      </nav>
      <span className="flex-1" />
      <Link to="/" className="flex items-center gap-2 rounded-full border border-white/30 px-3 py-2 text-sm font-bold text-white transition-colors hover:border-[#4CCC06] hover:text-[#4CCC06] sm:px-4" style={body} data-testid="shop-member-login-button">
        <LogIn size={16} /><span className="hidden sm:inline">Member login</span><span className="sm:hidden">Login</span>
      </Link>
      <Link to="/shop/cart" className="relative flex items-center gap-2 rounded-full bg-[#4CCC06] px-4 py-2 text-sm font-bold text-black transition-transform hover:-translate-y-0.5" style={body} data-testid="shop-cart-button">
        <ShoppingCart size={16} />Cart
        {count > 0 && <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-black px-1 text-[11px] text-white" data-testid="shop-cart-count">{count}</span>}
      </Link>
    </div>
    <nav className="flex gap-1 overflow-x-auto px-5 pb-2 md:hidden" data-testid="shop-nav-mobile">
      {GROUPS.map((g) => <NavLink key={g.slug} to={`/shop/c/${g.slug}`} data-testid={`shop-nav-mobile-${g.slug}`}
        className={({ isActive }) => `whitespace-nowrap rounded-full px-3 py-1.5 text-xs font-bold uppercase tracking-[1.5px] ${isActive ? "bg-white/10 text-[#4CCC06]" : "text-white/85"}`} style={head}>{g.label}</NavLink>)}
    </nav>
  </header>
);

const Footer = ({ info }) => (
  <footer className="mt-24 bg-[#0B0B0B] text-white/70" style={body}>
    <div className="mx-auto grid max-w-6xl gap-8 px-5 py-12 sm:grid-cols-3 sm:px-8">
      <div className="flex gap-3"><Truck className="shrink-0 text-[#4CCC06]" /><p className="text-sm">{info?.free_shipping_over_usd ? `Free US shipping on orders over ${money(info.free_shipping_over_usd)}. Otherwise ${money(info.shipping_flat_usd)} flat. Laser machines ship freight for $500 each.` : `Flat ${money(info?.shipping_flat_usd)} US shipping.`}</p></div>
      <div className="flex gap-3"><ShieldCheck className="shrink-0 text-[#4CCC06]" /><p className="text-sm">The same protective gear our laser technicians wear on every job.</p></div>
      <p className="text-sm">Secure card checkout by Stripe. Questions? <a className="text-[#4CCC06] underline" href="mailto:info@alrclean.com" data-testid="shop-contact-email">info@alrclean.com</a><br /><Link to="/terms" className="underline">Terms</Link> · <Link to="/privacy" className="underline">Privacy</Link><br /><Link to="/" className="mt-2 inline-block underline hover:text-[#4CCC06]" data-testid="shop-footer-portal-link">Back to ALR Ops member portal</Link></p>
    </div>
  </footer>
);

export const ShopLayout = ({ children, testId }) => {
  const { count } = useCart();
  const info = useInfo();
  return (
    <div className="min-h-screen" style={{ background: PAPER, color: INK, ...body }} data-testid={testId}>
      <Header count={count} />
      {info && !info.store_open && <div className="bg-[#FFD400] px-5 py-2 text-center text-sm font-bold text-black" data-testid="shop-closed-banner">The store is closed right now. You can browse, but checkout is paused.</div>}
      {children}
      <Footer info={info} />
    </div>
  );
};

export const ProductImage = ({ p, className = "" }) => {
  const src = p.image_url?.startsWith("/api/") ? `${BASE}${p.image_url}` : p.image_url;
  return src ? <img src={src} alt={p.name} className={`h-full w-full object-contain ${className}`} />
    : <div className={`flex h-full w-full items-center justify-center text-black/20 ${className}`}><ShieldCheck size={56} /></div>;
};
